import React, { useState, useEffect } from 'react';
import { Select, Space, Typography } from 'antd';
import { DatabaseOutlined } from '@ant-design/icons';
import { authService } from '../services/authService';
import { tenantService, Tenant } from '../services/tenantService';
import { useI18n } from '../hooks/useI18n';

const { Option } = Select;
const { Text } = Typography;

interface TenantSelectorProps {
  onTenantChange?: (tenantCode: string) => void;
}

const TenantSelector: React.FC<TenantSelectorProps> = ({ onTenantChange }) => {
  const { t } = useI18n();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userTenants, setUserTenants] = useState<string[]>([]);
  const [username, setUsername] = useState<string>('');

  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      
      // 获取当前用户信息
      const currentUser = authService.getStoredUser();
      const currentTenantCode = authService.getCurrentTenant();
      
      if (currentUser) {
        setUsername(currentUser.username || '');
        setIsSuperAdmin(currentUser.isSuperAdmin);
        
        // 获取用户所属的租户代码
        if (currentUser.tenants) {
          const userTenantCodes = currentUser.tenants.map(tn => tn.code);
          setUserTenants(userTenantCodes);
        }
        
        // 如果是超级管理员，获取所有租户
        if (currentUser.isSuperAdmin) {
          try {
            const allTenants = await tenantService.getAllTenants();
            setTenants(allTenants);
            
            // 设置当前选中的租户
            if (currentTenantCode) {
              setCurrentTenant(currentTenantCode);
            } else if (allTenants.length > 0) {
              // 如果没有当前租户，选择第一个可用的租户
              const firstCode = allTenants[0].code;
              setCurrentTenant(firstCode);
              authService.setCurrentTenant(firstCode);
              window.dispatchEvent(
                new CustomEvent('tenantChanged', { detail: { tenantCode: firstCode } }),
              );
            }
          } catch (error) {
            console.error('获取所有租户失败:', error);
            // 如果API调用失败，使用用户信息中的租户
            if (currentUser.tenants) {
              const userTenantsLocal = currentUser.tenants.map((tn: any) => ({
                id: tn.id,
                code: tn.code || `tenant_${tn.id}`,
                name: tn.name || `Tenant ${tn.id}`,
                api_generate_sql_endpoint: '',
                api_run_sql_endpoint: '',
                api_graphql_endpoint: ''
              }));
              setTenants(userTenantsLocal);
              if (currentTenantCode) {
                setCurrentTenant(currentTenantCode);
              } else if (userTenantsLocal.length > 0) {
                setCurrentTenant(userTenantsLocal[0].code);
                authService.setCurrentTenant(userTenantsLocal[0].code);
              }
            }
          }
        } else {
          // 普通用户使用用户信息中的租户
          if (currentUser.tenants && currentUser.tenants.length > 0) {
            const userTenantsLocal = currentUser.tenants.map((tn: any) => ({
              id: tn.id,
              code: tn.code || `tenant_${tn.id}`,
              name: tn.name || `Tenant ${tn.id}`,
              api_generate_sql_endpoint: '',
              api_run_sql_endpoint: '',
              api_graphql_endpoint: ''
            }));
            setTenants(userTenantsLocal);
            
            // 设置当前选中的租户
            if (currentTenantCode) {
              setCurrentTenant(currentTenantCode);
            } else if (userTenantsLocal.length > 0) {
              // 如果没有当前租户，选择第一个可用的租户
              setCurrentTenant(userTenantsLocal[0].code);
              authService.setCurrentTenant(userTenantsLocal[0].code);
            }
          } else {
            // 若用户对象没有 tenants 列表，则回退到用户的 tenant_code 或现有 currentTenant
            const fallbackCode = (currentUser as any).tenant_code || currentTenantCode || 'default';
            const singleTenant = [{
              id: 0,
              code: fallbackCode,
              name: fallbackCode,
              api_generate_sql_endpoint: '',
              api_run_sql_endpoint: '',
              api_graphql_endpoint: ''
            }] as any;
            setTenants(singleTenant);
            setCurrentTenant(fallbackCode);
            authService.setCurrentTenant(fallbackCode);
          }
        }
      } else {
        // 如果没有用户信息，设置默认租户
        const defaultTenants = [
          { id: 1, code: 'default', name: t('tenantSelector.defaultTenant'), api_generate_sql_endpoint: '', api_run_sql_endpoint: '', api_graphql_endpoint: '' }
        ];
        setTenants(defaultTenants as any);
        if (currentTenantCode) {
          setCurrentTenant(currentTenantCode);
        } else {
          setCurrentTenant('default');
          authService.setCurrentTenant('default');
        }
      }
      
    } catch (error) {
      console.error('加载租户数据失败:', error);
      // 即使出错也要设置默认租户数据，避免选择器一直处于加载状态
      const defaultTenants = [
        { id: 1, code: 'default', name: t('tenantSelector.defaultTenant'), api_generate_sql_endpoint: '', api_run_sql_endpoint: '', api_graphql_endpoint: '' }
      ];
      setTenants(defaultTenants as any);
      setCurrentTenant('default');
      authService.setCurrentTenant('default');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = (tenantCode: string) => {
    setCurrentTenant(tenantCode);
    authService.setCurrentTenant(tenantCode);
    
    // 通知父组件租户已更改
    if (onTenantChange) {
      onTenantChange(tenantCode);
    }
    
    // 触发自定义事件，通知其他组件租户已切换
    window.dispatchEvent(new CustomEvent('tenantChanged', { 
      detail: { tenantCode } 
    }));
    
    // 不再刷新页面，让父组件处理租户切换后的逻辑
  };

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <Space>
        <DatabaseOutlined style={{ color: '#1890ff' }} />
        <Text strong>{t('tenantSelector.label')}</Text>
        <Text type="secondary">{t('common.loading')}</Text>
      </Space>
    );
  }

  // 理论上通过回退逻辑不会出现无租户数据，这里保底显示 currentTenant
  if (tenants.length === 0) {
    const fallback = authService.getCurrentTenant() || 'default';
    return (
      <Space>
        <DatabaseOutlined style={{ color: '#1890ff' }} />
        <Text strong>{t('tenantSelector.label')}</Text>
        <Text>{fallback}</Text>
        {username && (
          <Text type="secondary" style={{ fontSize: '12px' }}> | {username}</Text>
        )}
      </Space>
    );
  }

  return (
    <Space>
      <DatabaseOutlined style={{ color: '#1890ff' }} />
      <Text strong>{t('tenantSelector.label')}</Text>
      <Select
        value={currentTenant}
        onChange={handleTenantChange}
        style={{ minWidth: 150 }}
        placeholder={t('tenantSelector.placeholder')}
        showSearch
        filterOption={(input, option) =>
          (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
        }
      >
        {tenants.map(tenant => (
          <Option key={tenant.code} value={tenant.code}>
            <Space>
              <span>{tenant.name}</span>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ({tenant.code})
              </Text>
            </Space>
          </Option>
        ))}
      </Select>

      {isSuperAdmin && (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('tenantSelector.adminMode')}
        </Text>
      )}
    </Space>
  );
};

export default TenantSelector;